import React, { useCallback } from 'react';
import { useField, useForm } from 'react-final-form';
import { observer } from 'mobx-react';
import dayjs from 'dayjs';
import { Button } from 'ksnm-common-ui/lib/Button';
import { Icon } from 'ksnm-common-ui/lib/Icon';

import { constantStore } from '@stores/constantStore';
import { reportPeriodsStore } from '@stores/dictionaryStores/reportPeriodsStore';
import { publicationRegisterStore } from '@stores/publicationRegisterStore';
import { userStore } from '@stores/userStore';
import { addZeroToDate } from '@utils';

import styles from './Info.module.scss';

const { CREATE } = constantStore.REPORT_PACKAGE_STATUS;

const InfoComponent = (): JSX.Element => {
  const { isFormed, setIsFormed } = publicationRegisterStore;
  const { user } = userStore;
  const { reportPeriods } = reportPeriodsStore;

  const { submit } = useForm();

  const {
    input: { onChange: changeCodeValue },
  } = useField('code');
  const {
    input: { value: organizationCodeValue },
  } = useField('organizationCode');
  const {
    input: { value: disclosureDataValue, onChange: changeDisclosureDataValue },
  } = useField('disclosureData');
  const {
    input: { value: creationDataValue, onChange: changeCreationDataValue },
  } = useField('creationData');
  const {
    input: { value: statusValue, onChange: changeStatusValue },
  } = useField('status');
  const {
    input: { value: userNameValue, onChange: changeUserNameValue },
  } = useField('username');
  const {
    input: { value: reportGroupCodeValue },
  } = useField('reportGroupCode');
  const {
    input: { value: yearValue },
  } = useField('year');
  const {
    input: { value: periodCodeValue },
  } = useField('periodCode');
  const {
    input: { value: correctionValue },
  } = useField('correction');

  const onApply = useCallback(() => {
    (async () => {
      const res = await submit();
      if (!res) {
        const createDate = dayjs(new Date()).format('DD.MM.YYYY');
        const year = dayjs(new Date(yearValue)).format('YYYY');
        const code = `${organizationCodeValue}_${reportGroupCodeValue}Y${year}P${periodCodeValue}V${correctionValue}`;

        const reportPeriod = reportPeriods.find(
          (period) => period.periodCode === periodCodeValue && period.reportGroupCode === reportGroupCodeValue,
        );

        changeCodeValue(code);
        changeDisclosureDataValue(
          reportPeriod
            ? `${addZeroToDate(reportPeriod.preparationDay)}.${addZeroToDate(
                reportPeriod.preparationMonthCode,
              )}.${year}`
            : '',
        );
        changeCreationDataValue(createDate);
        changeStatusValue(CREATE);
        changeUserNameValue(user?.fio ?? '');

        setIsFormed(true);
      }
    })();
  }, [
    changeCodeValue,
    changeCreationDataValue,
    changeDisclosureDataValue,
    changeStatusValue,
    changeUserNameValue,
    correctionValue,
    organizationCodeValue,
    periodCodeValue,
    reportGroupCodeValue,
    reportPeriods,
    setIsFormed,
    submit,
    user?.fio,
    yearValue,
  ]);

  return (
    <div className={styles.container}>
      {isFormed && (
        <div>
          <div className={styles.infoItem}>
            Дата подачи <span>{disclosureDataValue}</span>
          </div>
          <div className={styles.infoItem}>
            Дата создания<span>{creationDataValue}</span>
          </div>
          <div className={styles.infoItem}>
            Статус<span>{statusValue}</span>
          </div>
          <div className={styles.infoItem}>
            ФИО<span>{userNameValue}</span>
          </div>
          <div className={styles.infoItem}>
            Код организации<span>{organizationCodeValue}</span>
          </div>
        </div>
      )}

      {!isFormed ? (
        <Button className={styles.btn} icon={<Icon name="check" />} onClick={onApply} size="large">
          Применить
        </Button>
      ) : (
        <Button className={styles.btn} icon={<Icon name="save" />} onClick={submit} size="large">
          Сохранить
        </Button>
      )}
    </div>
  );
};

export const Info = observer(InfoComponent);
